import React, { useEffect, useState } from 'react';
import { Button, FlatList, HStack, Pressable, VStack } from 'native-base';
import { useShopStyles } from '~/shared/styles';
import Section from '~/shared/Section';
import { Caption } from '~/shared/Typography';
import { StoreModal } from './components/StoreModal';
import { CartItemCard } from './components/CartItemCard';
import {
    Item,
    Order,
    useDutchieStore,
    useGetCustomers,
    useI18n,
    useSpringBigStore
} from '@springbig/shared';
import { useAppNavigate } from '..';
import { TabSelector } from '~/pages/shop/components/TabSelector';
import { SpIcon } from '~/shared/Icon';
import { faShoppingCart } from '@fortawesome/free-solid-svg-icons';

export const ShoppingCartPage = () => {

    const { member } = useSpringBigStore();
    const [showStore, setShowStore] = useState(false);
    const {
        section_background,
        pressable_text,
        button_text,
        button_background,
        icon
    } = useShopStyles();
    const {
        fullfillmentOption,
        retailer,
        checkout,
        orders,
        setOrders,
        isBusy,
    } = useDutchieStore();
    const { mutate: getCustomers } = useGetCustomers();
    const { params, navigate } = useAppNavigate();
    const { t } = useI18n();

    useEffect(() => {

        if (member?.consumer.email_validated && member?.consumer.email) {
            getCustomers({
                'retailerId': retailer?.id,
                'filters': { 'email': member?.consumer.email }
            }, {
                onSuccess(data) {

                    const firstFive = data.customers.splice(0, 4);
                    const firstFiveOrders: Array<Order> = [];
                    firstFive.forEach((customer) => {

                        customer.orders?.forEach((order) => {

                            firstFiveOrders.push(order);
                        });
                    });
                    firstFiveOrders.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));
                    if (firstFiveOrders) {
                        setOrders(firstFiveOrders);
                    }
                },
            });
        }
    }, [member]);

    const tabs = [
        {
            name: 'current cart',
            isSelected: true,
        },
        {
            name: 'past orders',
            action: () => navigate(`w/${params.wallet_id}/shop/orders/`),
        },
    ];

    const onGoToCheckout = (): void => {

        if (checkout?.redirectUrl) {
            const url = new URL(checkout?.redirectUrl);
            if (member) {
                if (member.first_name) {
                    url.searchParams.append(
                        'firstName',
                        member?.first_name,
                    );
                }

                if (member.last_name) {
                    url.searchParams.append(
                        'lastName',
                        member?.last_name,
                    );
                }

                if (member.phone_number) {
                    url.searchParams.append('phone', member.phone_number);
                }

                if (member.birthday) {
                    const birthdaySplit = member.birthday.split('-');
                    const birthdateFormated =
                        birthdaySplit[1] +
                        birthdaySplit[2] +
                        birthdaySplit[0];
                    url.searchParams.append(
                        'birthdate',
                        birthdateFormated,
                    );
                }

                if (
                    member.consumer.email &&
                    member.consumer.email_validated
                ) {
                    url.searchParams.append(
                        'email',
                        member.consumer.email,
                    );
                }
            }


            const redirectUrl = `${window.location.origin}/w/${params.wallet_id}/shop/`;
            url.searchParams.append(
                'r',
                redirectUrl,
            );
            window.open(url);
        }
    };

    return (
        <Section h={'100%'}>
            <VStack h={'100%'} justifyContent={'space-between'} px={2} py={4}>
                { checkout?.items.length > 0 ?
                    <VStack space={4} alignItems={'center'} flex={2}>
                        <TabSelector tabs={tabs} /> 
                        <Pressable
                            bg={section_background}
                            py={1}
                            px={2}
                            borderRadius={4}
                            opacity={isBusy ? .7 : 1}
                            isDisabled={isBusy}
                            onPress={() => {

                                setShowStore(true);
                            }}
                        >
                            <HStack>
                                <Caption color={pressable_text}>
                                    { fullfillmentOption?.label }
                                </Caption>
                                <Caption> { t('shop.in') } </Caption>
                                <Caption color={pressable_text}>
                                    { retailer?.name }
                                </Caption>
                            </HStack>
                        </Pressable>

                        <FlatList
                            w={'100%'}
                            h={'60%'}
                            horizontal={false}
                            data={checkout?.items}
                            renderItem={({ item, index } : { item: Item, index: number}) =>

                                <CartItemCard item={item} key={index} />
                            }
                        />
                    </VStack>
                    :
                    <VStack
                        flex={1}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        space={4}
                    >
                        <TabSelector tabs={tabs} /> 
                        <VStack>
                            <SpIcon color={icon} icon={faShoppingCart} size={96} opacity={.3} />
                            <Caption>{ t('shop.cart_empty') }</Caption>
                        </VStack>
                        <VStack>
                            { orders && orders.length > 0 &&
                                <Button
                                    rounded={'2xl'}
                                    py={2}
                                    px={0}
                                    opacity={isBusy ? .7 : 1}
                                    isDisabled={isBusy}
                                    onPress={() => navigate(`w/${params.wallet_id}/shop/orders/`)}
                                >
                                    <Caption color={pressable_text}>
                                        { t('shop.see_past_orders') }
                                    </Caption>
                                </Button>
                            }
                        </VStack>
                    </VStack>
                }
                <VStack px={2} space={4} h={'120px'}>
                    { checkout?.items && checkout?.items.length > 0 && (
                        <Button
                            rounded={'2xl'}
                            py={4}
                            px={8}
                            background={button_background}
                            borderWidth={1}
                            borderColor={button_background}
                            opacity={isBusy ? .7 : 1}
                            isDisabled={isBusy}
                            onPress={onGoToCheckout}
                        >
                            <Caption color={button_text}>
                                { t('shop.go_to_checkout') }
                            </Caption>
                        </Button>
                    )}
                    <Button
                        rounded={'2xl'}
                        py={4}
                        px={8}
                        background={section_background}
                        borderWidth={1}
                        borderColor={pressable_text}
                        opacity={isBusy ? .7 : 1}
                        isDisabled={isBusy}
                        onPress={() => {

                            navigate(`w/${params.wallet_id}/shop`);
                        }}
                    >
                        <Caption color={pressable_text}>
                            { t('shop.back_to_store') }
                        </Caption>
                    </Button>
                </VStack>
            </VStack>
            { showStore && (
                <StoreModal
                    showStore={showStore}
                    setShowStore={() => {

                        setShowStore(!showStore);
                    }}
                />
            )}
        </Section>
    );
};
